/**
 * Guardian Service
 *
 * Handles guardian (parent/teacher) management for SciQuest Heroes.
 * Guardians use Supabase Auth for authentication.
 */

import { getSupabaseClient, hasSupabaseConfig } from './supabase-client';
import type { Guardian, GuardianSignupData, GuardianType, GuardianLoginCredentials } from '../utils/types';
import { userCache, generateCacheKey } from '@utils/cache-helper';
import { setGuardianSession, getGuardianSession, clearGuardianSession } from '@utils/session-helper';
import { posthogCapture, posthogIdentify, posthogReset } from './posthog-service';

export class GuardianService {
  /**
   * Create a new guardian (parent or teacher) account
   */
  static async createGuardian(
    signupData: GuardianSignupData
  ): Promise<{ guardian: Guardian | null; error: Error | null }> {
    try {
      if (!hasSupabaseConfig()) {
        // Mock mode
        const mockGuardian: Guardian = {
          id: `mock-${Date.now()}`,
          created_at: new Date().toISOString(),
          auth_user_id: `auth-${Date.now()}`,
          guardian_type: signupData.guardian_type,
          full_name: signupData.full_name,
          email: signupData.email,
        };
        return { guardian: mockGuardian, error: null };
      }

      const supabase = getSupabaseClient();

      // First, sign up with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: signupData.email,
        password: signupData.password,
        options: {
          data: {
            full_name: signupData.full_name,
            guardian_type: signupData.guardian_type,
          },
        },
      });

      if (authError || !authData.user) {
        return { guardian: null, error: authError };
      }

      // Then, create guardian record in guardians table
      const { data: guardianData, error: guardianError } = await supabase
        .from('guardians')
        .insert({
          auth_user_id: authData.user.id,
          guardian_type: signupData.guardian_type,
          full_name: signupData.full_name,
          email: signupData.email,
        })
        .select()
        .single();

      if (guardianError) {
        return { guardian: null, error: guardianError };
      }

      const guardian = guardianData as Guardian;
      posthogIdentify(guardian.id, { email: guardian.email, name: guardian.full_name, guardian_type: guardian.guardian_type });
      posthogCapture('guardian signed up', { guardian_type: guardian.guardian_type, login_type: 'email' });
      return { guardian, error: null };
    } catch (error) {
      return { guardian: null, error: error as Error };
    }
  }

  /**
   * Login with email and password (standard Supabase Auth)
   */
  static async login(
    credentials: GuardianLoginCredentials
  ): Promise<{ guardian: Guardian | null; error: Error | null }> {
    try {
      if (!hasSupabaseConfig()) {
        const mockGuardian: Guardian = {
          id: 'mock-guardian-123',
          created_at: new Date().toISOString(),
          auth_user_id: 'auth-123',
          guardian_type: 'parent',
          full_name: 'Mock Parent',
          email: credentials.email,
        };
        return { guardian: mockGuardian, error: null };
      }

      const supabase = getSupabaseClient();

      // Sign in with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (authError || !authData.user) {
        return { guardian: null, error: authError || new Error('Login failed') };
      }

      // Get guardian record from guardians table
      const result = await this.getGuardianByAuthId(authData.user.id);
      if (result.guardian) {
        posthogIdentify(result.guardian.id, { email: result.guardian.email, name: result.guardian.full_name, guardian_type: result.guardian.guardian_type });
        posthogCapture('guardian logged in', { guardian_type: result.guardian.guardian_type, login_type: 'email' });
      }
      return result;
    } catch (error) {
      return { guardian: null, error: error as Error };
    }
  }

  /**
   * Login with Google OAuth (for teachers/parents)
   */
  static async loginWithGoogle(guardianType: GuardianType): Promise<{ error: Error | null }> {
    try {
      if (!hasSupabaseConfig()) {
        throw new Error('OAuth not available in mock mode');
      }

      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback.html?type=${guardianType}`,
        },
      });

      if (error) {
        return { error };
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  /**
   * Get guardian by auth user_id (with caching)
   */
  static async getGuardianByAuthId(
    authUserId: string
  ): Promise<{ guardian: Guardian | null; error: Error | null }> {
    try {
      // Check cache first
      const cacheKey = generateCacheKey('guardian', authUserId);
      const cached = userCache.get(cacheKey);
      if (cached) {
        return { guardian: cached as Guardian, error: null };
      }

      if (!hasSupabaseConfig()) {
        const mockGuardian: Guardian = {
          id: 'mock-guardian-123',
          created_at: new Date().toISOString(),
          auth_user_id: authUserId,
          guardian_type: 'parent',
          full_name: 'Mock Parent',
          email: 'parent@example.com',
        };
        userCache.set(cacheKey, mockGuardian);
        return { guardian: mockGuardian, error: null };
      }

      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from('guardians')
        .select('*')
        .eq('auth_user_id', authUserId)
        .single();

      if (error) {
        return { guardian: null, error };
      }

      const guardian = data as Guardian;
      // Cache the result
      userCache.set(cacheKey, guardian);
      return { guardian, error: null };
    } catch (error) {
      return { guardian: null, error: error as Error };
    }
  }

  /**
   * Get current authenticated guardian.
   * Fast path: uses localStorage cache so pages don't need a DB round-trip on
   * every navigation. Falls back to DB query when cache is missing or expired.
   */
  static async getCurrentGuardian(): Promise<{ guardian: Guardian | null; error: Error | null }> {
    try {
      if (!hasSupabaseConfig()) {
        return { guardian: null, error: new Error('Not authenticated') };
      }

      const supabase = getSupabaseClient();

      // getSession() reads from localStorage — no network round-trip needed
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        clearGuardianSession();
        return { guardian: null, error: new Error('Not authenticated') };
      }

      // Return cached guardian if it belongs to the current auth user
      const cached = getGuardianSession();
      if (cached && cached.auth_user_id === session.user.id) {
        return { guardian: cached, error: null };
      }

      // Cache miss — fetch from DB and persist for future page loads
      const result = await this.getGuardianByAuthId(session.user.id);
      if (result.guardian) {
        setGuardianSession(result.guardian);
        // Re-identify covers returning visitors and Google OAuth completions
        posthogIdentify(result.guardian.id, { email: result.guardian.email, name: result.guardian.full_name, guardian_type: result.guardian.guardian_type });
      }
      return result;
    } catch (error) {
      return { guardian: null, error: error as Error };
    }
  }

  /**
   * Update guardian information
   */
  static async updateGuardian(
    guardianId: string,
    updates: Partial<Guardian>
  ): Promise<{ guardian: Guardian | null; error: Error | null }> {
    try {
      if (!hasSupabaseConfig()) {
        const mockGuardian: Guardian = {
          id: guardianId,
          created_at: new Date().toISOString(),
          auth_user_id: 'auth-123',
          guardian_type: updates.guardian_type || 'parent',
          full_name: updates.full_name || 'Mock Guardian',
          email: updates.email || 'guardian@example.com',
          avatar: updates.avatar,
        };
        // Invalidate cache
        userCache.clear(generateCacheKey('guardian', mockGuardian.auth_user_id));
        return { guardian: mockGuardian, error: null };
      }

      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from('guardians')
        .update(updates)
        .eq('id', guardianId)
        .select()
        .single();

      if (error) {
        return { guardian: null, error };
      }

      const guardian = data as Guardian;
      // Invalidate in-memory cache and refresh localStorage session
      userCache.clear(generateCacheKey('guardian', guardian.auth_user_id));
      setGuardianSession(guardian);
      return { guardian, error: null };
    } catch (error) {
      return { guardian: null, error: error as Error };
    }
  }

  /**
   * Logout guardian
   */
  static async logout(): Promise<{ error: Error | null }> {
    try {
      posthogReset();
      clearGuardianSession();

      if (hasSupabaseConfig()) {
        const supabase = getSupabaseClient();
        const { error } = await supabase.auth.signOut();
        if (error) {
          return { error };
        }
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  /**
   * Redirect to appropriate dashboard based on guardian type
   */
  static redirectToDashboard(guardianType: GuardianType): void {
    const dashboardUrls: Record<GuardianType, string> = {
      parent: '/parent/dashboard.html',
      teacher: '/teacher/dashboard.html',
    };

    window.location.href = dashboardUrls[guardianType];
  }
}

export default GuardianService;
